/**
 * Panel Schema — Declarative types for building design panels from data description
 * Schema IS the single source of truth: defines data model, defaults, and UI layout
 */

// ── Field types ──

export type FieldType = 'color' | 'width' | 'opacity' | 'select' | 'custom';

export interface FieldDef {
  key: string;
  type: FieldType;
  label?: string;
  default?: any;
  /** For custom type: multiple default values keyed by field name */
  defaults?: Record<string, any>;
  /** Identifier for custom renderer lookup */
  customId?: string;
  /** For select type */
  options?: string[];
  /** For width/slider type */
  min?: number;
  max?: number;
  step?: number;
}

export interface RowDef {
  label: string;
  fields: FieldDef[];
}

export interface SectionDef {
  title: string;
  rows: RowDef[];
}

export interface SchemaPreset {
  name: string;
  values: Record<string, any>;
}

export interface PanelSchema {
  sections: SectionDef[];
  storageKey: string;
  eventType: string;
  presets?: SchemaPreset[];
  /** Default values for fields not shown in UI */
  hiddenDefaults?: Record<string, any>;
}

/**
 * Extract default values object from schema definition.
 * Walks all sections → rows → fields, collects defaults.
 * Custom fields contribute multiple keys via `defaults`.
 * Hidden defaults are merged last.
 */
export function buildDefaults(schema: PanelSchema): Record<string, any> {
  const result: Record<string, any> = {};

  for (const section of schema.sections) {
    for (const row of section.rows) {
      for (const field of row.fields) {
        if (field.type === 'custom' && field.defaults) {
          Object.assign(result, field.defaults);
        } else if (field.default !== undefined) {
          result[field.key] = field.default;
        }
      }
    }
  }

  if (schema.hiddenDefaults) {
    Object.assign(result, schema.hiddenDefaults);
  }

  return result;
}
